import { odooApi } from '../index';
import { Partner, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { 
  transformOdooPartnerToPartner,
  transformOdooPaginatedResponse,
  extractErrorMessage 
} from '../transforms';

/**
 * Partners API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all partner-related API endpoints.
 * Connected to real Odoo API at /api/moys/partners
 */

export const partnersApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all partners from Odoo with pagination
    getPartners: builder.query<PaginatedResponse<Partner>, { 
      lang?: 'ar' | 'en' | 'ku';
      page?: number;
      pageSize?: number;
    }>({
      query: ({ lang = 'ar', page = 1, pageSize = 10 }) => {
        const odooLang = getOdooLang(lang);
        
        // Backend expects page and page_size via _get_pagination_params
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });
        
        return {
          url: `/api/moys/partners?${params.toString()}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any, meta, arg) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }
          
          return transformOdooPaginatedResponse(
            response,
            (item: any) => transformOdooPartnerToPartner(item) as Partner,
            arg.page || 1,
            arg.pageSize || 10,
            arg.lang || 'ar'
          );
        } catch (error) {
          console.error('[Partners API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Partner' as const, id })),
              { type: 'Partner', id: 'LIST' },
            ]
          : [{ type: 'Partner', id: 'LIST' }],
    }),

    // Get single partner by ID or slug
    getPartner: builder.query<Partner, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, api, extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);
        
        try {
          // Try parsing as integer first (for ID-based lookup)
          const partnerId = parseInt(id, 10);
          const isNumericId = !isNaN(partnerId);
          
          if (isNumericId) {
            // Use the ID-based endpoint
            const result = await baseQuery({
              url: `/api/moys/partners/${partnerId}?lang=${odooLang}`,
              method: 'POST',
            });
            
            if (result.error) {
              console.error('[Partners API] Partner endpoint error:', result.error);
              return { error: result.error as any };
            }
            
            const response = result.data as any;
            
            if (response && response.success && response.data) {
              // Transform the single partner response
              const partner = transformOdooPartnerToPartner(response.data);
              console.log('[Partners API] Found partner via ID endpoint:', partner.id);
              return { data: partner as Partner };
            }
          }
          
          // If ID lookup failed or it's a slug, search through paginated results
          console.log('[Partners API] Searching for partner by slug:', id);
          
          // Fetch partners with a large page size to find the slug
          let page = 1;
          const pageSize = 100;
          let foundPartner: Partner | null = null;
          
          while (!foundPartner && page <= 10) { // Limit to 10 pages (1000 partners max)
            const listResult = await baseQuery({
              url: `/api/moys/partners?lang=${odooLang}&page=${page}&page_size=${pageSize}`,
              method: 'POST',
            });
            
            if (listResult.error) {
              console.error('[Partners API] Partners list error:', listResult.error);
              break;
            }
            
            const listResponse = listResult.data as any;
            
            if (!listResponse || !listResponse.success || !listResponse.data) {
              break;
            }
            
            // Search for partner with matching slug or ID
            const partners = listResponse.data.map((item: any) => transformOdooPartnerToPartner(item) as Partner);
            foundPartner = partners.find((p: Partner) => p.slug === id || p.id === id) || null;
            
            if (foundPartner) {
              console.log('[Partners API] Found partner via slug search:', foundPartner.id);
              return { data: foundPartner };
            }
            
            // Check if there are more pages
            if (!listResponse.pagination?.has_next) {
              break;
            }
            
            page++;
          }
          
          // Partner not found
          console.warn('[Partners API] Partner not found:', id);
          return { error: { status: 404, data: 'Partner not found' } as any };
        } catch (error) {
          console.error('[Partners API] Get single partner error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Partner', id }],
    }),
  }),
});

export const {
  useGetPartnersQuery,
  useGetPartnerQuery,
} = partnersApi;


